import React from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { useForm, ValidationError } from '@formspree/react';
import './QuoteForm.css';
import '../scrollReveal.css';
import useScrollReveal from '../hooks/useScrollReveal';

const FORM_ID = 'mqaljdez';

const QuoteForm = () => {
  const [formState, handleSubmit] = useForm(FORM_ID);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    car: '',
    service: 'carbon-fiber-aero',
    details: '',
  });
  const [openSuccess, setOpenSuccess] = React.useState(false);
  const [openError, setOpenError] = React.useState(false);
  const sectionRef = useScrollReveal();

  React.useEffect(() => {
    if (formState.succeeded) {
      setOpenSuccess(true);
      setFormData({ name: '', email: '', car: '', service: 'carbon-fiber-aero', details: '' });
    }
  }, [formState.succeeded]);

  React.useEffect(() => {
    if (formState.errors && formState.errors.length > 0) setOpenError(true);
  }, [formState.errors]);

  return (
    <>
      <div id="quote-form" className="quote-form-container" ref={sectionRef}>
        <div className="quote-form-inner">
          <div className="quote-form-box" data-reveal="zoom" data-delay="100">
            <div className="form-eyebrow" data-reveal="fade-down" data-delay="200">Start Your Build</div>

            <Typography variant="h4" component="h2" className="form-title" data-reveal="fade-up" data-delay="250">
              Request a Custom Build
            </Typography>
            <Typography variant="subtitle1" className="form-subtitle" data-reveal="fade-up" data-delay="300">
              Tell us about your car and what you're looking to build. We respond within 24 hours.
            </Typography>

            <form onSubmit={handleSubmit} className="quote-form">
              <input type="hidden" name="_subject" value="New Build Request - KING3DMFG" />

              <div data-reveal="fade-right" data-delay="350">
                <FormControl fullWidth margin="normal">
                  <TextField name="name" label="Your Name" variant="outlined" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </FormControl>
              </div>

              <div data-reveal="fade-right" data-delay="400">
                <FormControl fullWidth margin="normal">
                  <TextField name="email" label="Email Address" type="email" variant="outlined" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  <ValidationError prefix="Email" field="email" errors={formState.errors} />
                </FormControl>
              </div>

              <div data-reveal="fade-right" data-delay="420">
                <FormControl fullWidth margin="normal">
                  <TextField name="car" label="Your Car (Year, Make, Model)" variant="outlined" value={formData.car}
                    onChange={(e) => setFormData({ ...formData, car: e.target.value })}
                    placeholder="e.g. 2021 Lamborghini Huracán EVO" />
                </FormControl>
              </div>

              <div data-reveal="fade-right" data-delay="450">
                <FormControl fullWidth margin="normal">
                  <InputLabel id="service-label">Type of Build</InputLabel>
                  <Select labelId="service-label" name="service" value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    label="Type of Build" variant="outlined">
                    <MenuItem value="carbon-fiber-aero">Carbon Fiber Aero Components</MenuItem>
                    <MenuItem value="custom-body-kit">Custom Body Kit / Wide-Body</MenuItem>
                    <MenuItem value="interior-trim">Carbon Fiber Interior Trim</MenuItem>
                    <MenuItem value="intake-cooling">Intake & Cooling Systems</MenuItem>
                    <MenuItem value="3d-scanning">3D Scanning & Part Replication</MenuItem>
                    <MenuItem value="prototype">Prototype / R&D Support</MenuItem>
                    <MenuItem value="consultation">General Consultation</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div data-reveal="fade-right" data-delay="500">
                <FormControl fullWidth margin="normal">
                  <TextField name="message" label="Tell Us About Your Build" multiline rows={5} variant="outlined"
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    placeholder="What are you trying to create? Reference any inspiration, fitment concerns, or timeline..." />
                  <ValidationError prefix="Message" field="message" errors={formState.errors} />
                </FormControl>
              </div>

              <div data-reveal="fade-up" data-delay="550">
                <Button type="submit" variant="contained" size="large" className="submit-button"
                  fullWidth disabled={formState.submitting}>
                  {formState.submitting ? 'Sending…' : 'Submit Build Request'}
                </Button>
              </div>

              <ValidationError errors={formState.errors} />
            </form>
          </div>
        </div>
      </div>

      <footer className="quote-footer">
        &copy; {new Date().getFullYear()} KING3DMFG — All Rights Reserved
      </footer>

      <Snackbar open={openSuccess} autoHideDuration={4000} onClose={() => setOpenSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSuccess(false)} severity="success" variant="filled">
          Request received — we'll be in touch within 24 hours.
        </Alert>
      </Snackbar>

      <Snackbar open={openError} autoHideDuration={5000} onClose={() => setOpenError(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setOpenError(false)} severity="error" variant="filled">
          Something went wrong. Please check your details and try again.
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuoteForm;
